<script lang="ts">
import { computed, toRef } from 'vue';

import { useTabContent } from './composables/use-tab-content';
import PhxsgTabPanel from './phxsg-tab-panel.vue';

import type { PhxsgTabsLayout, PhxsgTabsOrientation, PhxsgTabsPanelData, PhxsgTabsTransitionMode } from './tabs.types';

export interface PhxsgTabContentProps {
  instanceId: string;
  panels: PhxsgTabsPanelData[];
  panel?: PhxsgTabsPanelData | null;
  teleportTarget?: string | HTMLElement | null;
  orientation?: PhxsgTabsOrientation;
  layout: PhxsgTabsLayout;
  transitionName?: string;
  transitionMode?: PhxsgTabsTransitionMode;
  contentOffset?: number;
}
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<PhxsgTabContentProps>(), {
  panel: null,
  teleportTarget: null,
  orientation: 'horizontal',
  transitionName: 'phxsg-tabs__panel-',
  transitionMode: 'default',
  contentOffset: 40,
});

const emit = defineEmits<{
  'tab-content-change': [element: HTMLElement];
}>();

const { vNodeRef, wrapperHeight, calculatedContentOffset } = useTabContent(
  {
    currentPanel: toRef(props, 'panel'),
    panels: toRef(props, 'panels'),
    orientation: toRef(props, 'orientation'),
    contentOffset: toRef(props, 'contentOffset'),
  },
  { emit },
);

const panelClasses = computed(() => [
  'phxsg-tabs__panels',
  {
    'phxsg-tabs__panels--vertical': props.orientation === 'vertical',
    'phxsg-tabs__panels--boxed': props.layout === 'stretch',
  },
]);
</script>

<template>
  <Teleport v-if="props.teleportTarget" :to="props.teleportTarget">
    <div
      :class="panelClasses"
      :style="{
        height: `${wrapperHeight}px`,
        '--_comp-tabs-panel-offset': calculatedContentOffset,
      }"
    >
      <Transition :name="props.transitionName" :mode="props.transitionMode">
        <KeepAlive>
          <PhxsgTabPanel
            v-if="props.panel"
            :key="props.panel.id"
            ref="vNodeRef"
            :instance-id="props.instanceId"
            :panel="props.panel"
            :orientation="props.orientation"
            :layout="props.layout"
          />
        </KeepAlive>
      </Transition>
    </div>
  </Teleport>

  <div
    v-else
    :class="panelClasses"
    :style="{
      height: `${wrapperHeight}px`,
      '--_comp-tabs-panel-offset': calculatedContentOffset,
    }"
  >
    <Transition :name="props.transitionName" :mode="props.transitionMode">
      <KeepAlive>
        <PhxsgTabPanel
          v-if="props.panel"
          :key="props.panel.id"
          ref="vNodeRef"
          :instance-id="props.instanceId"
          :panel="props.panel"
          :orientation="props.orientation"
          :layout="props.layout"
        />
      </KeepAlive>
    </Transition>
  </div>
</template>
